import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Mic, Play, Pause, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { triggerHaptic } from '@/lib/interactions/haptics';
import { useTranslation } from 'react-i18next';
import { playSystemClip } from '@/config/systemAudio';

type RecordingState = 'idle' | 'recording' | 'recorded' | 'sending';

interface VoiceAgentRecorderProps {
  onSend: (audioBlob: Blob, duration: number) => void;
  onCancel: () => void;
  questionsRemaining: number;
  disabled?: boolean;
  isUploading?: boolean;
}

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const getSupportedMimeType = (): string => {
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg',
  ];

  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  return '';
};

export const VoiceAgentRecorder: React.FC<VoiceAgentRecorderProps> = ({
  onSend,
  onCancel,
  questionsRemaining,
  disabled = false,
  isUploading = false,
}) => {
  const { t } = useTranslation();
  const [state, setState] = useState<RecordingState>('idle');
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const startTimeRef = useRef<number>(0);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [audioUrl]);

  // Track previous isUploading value to detect transition
  const prevIsUploadingRef = useRef(isUploading);

  // Reset state only when upload completes (transitions from true to false)
  useEffect(() => {
    const wasUploading = prevIsUploadingRef.current;
    const nowNotUploading = !isUploading;

    if (wasUploading && nowNotUploading) {
      setState('idle');
      setAudioBlob(null);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
      setRecordingTime(0);
    }

    prevIsUploadingRef.current = isUploading;
  }, [isUploading, audioUrl]);

  const startRecording = useCallback(async () => {
    if (disabled || questionsRemaining <= 0) return;

    try {
      triggerHaptic('medium');

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getSupportedMimeType();

      const options: MediaRecorderOptions = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);

      audioChunksRef.current = [];
      mediaRecorderRef.current = mediaRecorder;
      startTimeRef.current = Date.now();

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const duration = Math.round((Date.now() - startTimeRef.current) / 1000);
        const blob = new Blob(audioChunksRef.current, { type: mimeType || 'audio/webm' });

        if (blob.size === 0) {
          setState('idle');
          stream.getTracks().forEach(track => track.stop());
          return;
        }

        setAudioBlob(blob);

        if (audioUrl) URL.revokeObjectURL(audioUrl);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        setRecordingTime(duration);
        setState('recorded');

        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setState('recording');
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('Failed to start recording:', error);
      triggerHaptic('error');
      toast.error(t('agent.micPermissionDenied', 'Please allow microphone access'));
      playSystemClip('micDenied');
    }
  }, [disabled, questionsRemaining, audioUrl, t]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && state === 'recording') {
      triggerHaptic('light');

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      if (mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.requestData();
      }

      mediaRecorderRef.current.stop();
    }
  }, [state]);

  const playPreview = useCallback(() => {
    if (!audioUrl) return;

    triggerHaptic('light');

    if (audioRef.current) {
      audioRef.current.pause();
    }

    const audio = new Audio(audioUrl);
    audioRef.current = audio;

    audio.onplay = () => setIsPlaying(true);
    audio.onended = () => setIsPlaying(false);
    audio.onpause = () => setIsPlaying(false);

    audio.play().catch(console.error);
  }, [audioUrl]);

  const pausePreview = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  }, []);

  const isDisabled = disabled || questionsRemaining <= 0;

  const handleSend = useCallback(() => {
    // Bug 4: harden against rapid double-tap. The `disabled` prop and the
    // 'sending' state already gate the button, but a quick second tap during
    // the props/state update window could otherwise re-fire onSend.
    if (state !== 'recorded') return;
    if (isDisabled || isUploading) return;

    if (audioBlob) {
      triggerHaptic('success');
      setState('sending');
      onSend(audioBlob, recordingTime);
    }
  }, [state, audioBlob, recordingTime, onSend, isDisabled, isUploading]);

  const handleTryAgain = useCallback(() => {
    triggerHaptic('light');

    if (audioUrl) URL.revokeObjectURL(audioUrl);
    if (audioRef.current) audioRef.current.pause();

    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    setState('idle');
    onCancel();
  }, [audioUrl, onCancel]);

  return (
    <div className="flex flex-col items-center justify-center py-8 px-4 w-full">
      {/* Daily Limit Indicator */}
      <div className="flex items-center gap-2 mb-8">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className={cn(
              "w-4 h-4 rounded-full transition-colors",
              i < (3 - questionsRemaining)
                ? "bg-muted-foreground"
                : "bg-primary"
            )}
          />
        ))}
      </div>

      {/* Main Recording Button / Preview Area */}
      <div className="flex flex-col items-center gap-6">
        {state === 'idle' && (
          <>
            <button
              onTouchStart={startRecording}
              onMouseDown={startRecording}
              onTouchEnd={stopRecording}
              onMouseUp={stopRecording}
              onMouseLeave={stopRecording}
              disabled={isDisabled}
              className={cn(
                "w-[120px] h-[120px] rounded-full flex items-center justify-center transition-all",
                "shadow-lg active:scale-95",
                isDisabled
                  ? "bg-muted cursor-not-allowed"
                  : "bg-green-500 hover:bg-green-600 active:bg-green-700"
              )}
              aria-label={t('agent.holdToSpeak', 'Hold to speak')}
            >
              <Mic
                className={cn(
                  "w-12 h-12",
                  isDisabled ? "text-muted-foreground" : "text-white"
                )}
              />
            </button>
            <p className={cn(
              "text-lg font-medium",
              isDisabled ? "text-muted-foreground" : "text-foreground"
            )}>
              {isDisabled ? t('agent.noQuestionsLeft', 'No questions left today') : t('agent.holdToSpeak', 'Hold to speak')}
            </p>
          </>
        )}

        {state === 'recording' && (
          <>
            <button
              onTouchEnd={stopRecording}
              onMouseUp={stopRecording}
              className={cn(
                "w-[120px] h-[120px] rounded-full flex items-center justify-center",
                "bg-red-500 shadow-lg animate-pulse"
              )}
              aria-label={t('agent.releaseToStop', 'Release to stop')}
            >
              <Mic className="w-12 h-12 text-white" />
            </button>

            <p className="text-2xl font-bold text-foreground tabular-nums">
              {formatTime(recordingTime)}
            </p>

            <div className="flex items-center justify-center gap-1 h-12">
              {Array.from({ length: 7 }).map((_, i) => (
                <div
                  key={i}
                  className="w-2 bg-red-500 rounded-full animate-waveform-bar"
                  style={{
                    animationDelay: `${i * 0.1}s`,
                    height: '100%',
                  }}
                />
              ))}
            </div>
          </>
        )}

        {state === 'recorded' && (
          <>
            <button
              onClick={isPlaying ? pausePreview : playPreview}
              className={cn(
                "w-[120px] h-[120px] rounded-full flex items-center justify-center",
                "bg-primary shadow-lg transition-all active:scale-95"
              )}
              aria-label={isPlaying ? t('agent.pausePreview', 'Pause preview') : t('agent.playPreview', 'Play preview')}
            >
              {isPlaying ? (
                <Pause className="w-12 h-12 text-primary-foreground" />
              ) : (
                <Play className="w-12 h-12 text-primary-foreground ml-2" />
              )}
            </button>

            <p className="text-xl font-medium text-foreground tabular-nums">
              {formatTime(recordingTime)}
            </p>

            <div className="flex items-center gap-6 mt-4">
              <button
                onClick={handleTryAgain}
                className={cn(
                  "w-16 h-16 rounded-full flex items-center justify-center",
                  "bg-destructive shadow-md transition-all active:scale-95"
                )}
                aria-label={t('agent.tryAgain', 'Try again')}
              >
                <X className="w-8 h-8 text-destructive-foreground" />
              </button>

              <button
                onClick={handleSend}
                disabled={isDisabled || isUploading}
                className={cn(
                  "w-16 h-16 rounded-full flex items-center justify-center",
                  "bg-green-500 shadow-md transition-all active:scale-95"
                )}
                aria-label={t('agent.send', 'Send')}
              >
                <Check className="w-8 h-8 text-white" />
              </button>
            </div>
          </>
        )}

        {state === 'sending' && (
          <div className="flex flex-col items-center gap-4">
            <div className="w-[120px] h-[120px] rounded-full flex items-center justify-center bg-muted">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-500 border-t-transparent" />
            </div>
            <p className="text-lg font-medium text-muted-foreground">
              {t('agent.sending', 'Sending...')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceAgentRecorder;
